import { useCallback, useMemo, useState } from 'react';
import NextImage from 'next/image';
import { useSelector } from 'react-redux';
import { MapRef, Marker } from 'react-map-gl/maplibre';
import { bbox } from '@turf/turf';
import { FeatureCollection } from 'geojson';

import { Map } from '@/core/components/Map';
import { MapGeofeatures } from '@/core/components/MapGeofeatures';
import { useDecodedCategories } from '@/core/hooks/useDecodedCategories';
import { useLoadMapIcons } from '@/core/hooks/useLoadMapIcons';
import { mapCategories } from '@/core/constants/reports';
import { getAppliedFilterSelector } from '@/core/store/reducers/filtersSlice';
import { FilterType } from '@/core/interfaces/filters';
import { LocationFilterItemType } from '@/core/components/MapFilters/LocationFilter/LocationFilter.types';

import { getAllGeofeaturesListSelector } from '@/features/Geofeatures/store';
import { GeofeatureItem } from '@/features/Geofeatures/interfaces';
import { getReportCategoriesNames } from '@/features/Reports/helpers';

import { getTransformedLocationFilterData } from '@/utils/locations';

import { MapWrapper } from './LocationMap.styles';
import { LocationMapProps } from './LocationMap.types';

export const LocationMap = ({ report }: LocationMapProps) => {
  const [mapRef, setMapRef] = useState<MapRef | null>(null);

  const geofeaturesList = useSelector(getAllGeofeaturesListSelector);
  const activeLocationFilter = useSelector(state =>
    getAppliedFilterSelector(state, FilterType.LOCATION)
  );

  useLoadMapIcons(mapRef?.getMap() || null);

  const categories = useDecodedCategories(getReportCategoriesNames(report.category || []));

  const markerIcon = useMemo(() => {
    return (
      report.category[0]?.mainCategory.subcategories[0]?.subCategorySvg ??
      mapCategories(categories[0]).imgSrc
    );
  }, [categories, report]);

  const geofeaturesToDisplay = useMemo(
    () => [
      ...geofeaturesList.filter(geofeature => geofeature.visibleOnMap),
      ...activeLocationFilter.reduce((acc, filter) => {
        const filterValue = getTransformedLocationFilterData(filter.value as string);

        if (filterValue.locationItemType !== LocationFilterItemType.COUNTRY) {
          return [
            ...acc,
            {
              color: filterValue.value.properties.color,
              folderId: filterValue.value.properties.folder!,
              id: filterValue.value.properties.id,
              name: filterValue.value.properties.name,
              tags: filterValue.value.properties.tags,
              description: filterValue.value.properties.description,
              icon: filterValue.value.properties.icon,
              visibleOnMap: true,
              geoJSONData: filterValue.value,
            } satisfies GeofeatureItem,
          ];
        }

        return acc;
      }, [] as Array<GeofeatureItem>),
    ],
    [activeLocationFilter, geofeaturesList]
  );

  const handleMapLoad = useCallback(() => {
    if (!report.location.length) {
      return;
    }

    const featureCollection: FeatureCollection = {
      type: 'FeatureCollection',
      features: report.location.map(reportLocation => ({
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [reportLocation.longitude, reportLocation.latitude],
        },
        properties: {},
      })),
    };

    // @ts-expect-error bbox has correct values
    mapRef?.getMap().fitBounds(bbox(featureCollection), {
      padding: 20,
      maxZoom: 10,
      duration: 10,
    });
  }, [mapRef, report]);

  return (
    <MapWrapper>
      <Map
        key={report.reportId}
        ref={setMapRef}
        viewState={{
          longitude: report.location[0].longitude,
          latitude: report.location[0].latitude,
          zoom: 10,
        }}
        onMapLoad={handleMapLoad}
      >
        {report.location.map(reportLocation => (
          <Marker
            key={reportLocation.address}
            longitude={reportLocation.longitude}
            latitude={reportLocation.latitude}
            anchor="bottom"
          >
            {markerIcon && (
              <NextImage
                src={markerIcon}
                alt="map-icon"
                width={40}
                height={46}
              />
            )}
          </Marker>
        ))}
        <MapGeofeatures geofeatures={geofeaturesToDisplay} />
      </Map>
    </MapWrapper>
  );
};
